import { Headline } from "@/components/Global/Headline/Headline";
import { Section } from "@/components/Global/Section/Section";
import {
  RecipeCard,
  RecipeElementType,
} from "@/components/RecipeCard/RecipeCard";
import { Modul_Rezept_Grid } from "@/wp/types/acf-module-types";
import { fetchRecipes, RecipeGridResponseType } from "./fetchRecipes";
import { useRouter } from "next/router";
import { ImageType } from "@/types/baseTypes";
import { GridLoadMoreButton } from "@/components/Grid/LoadMore/GridLoadMoreButton";
import { GridFilters } from "@/components/Grid/Filters/GridFilters";
import Grid, { TipTopfSortType } from "@/components/Grid/Grid";
import GridContent from "@/components/Grid/Content/GridContent";
import GridItem from "@/components/Grid/Item/GridItem";
import { SortedCategoriesType } from "@/utils/rezeptAPI/getRecipeFilterOptions";
import { getFiltersFromBrowser } from "./queryParamsHelper";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { RecipeCardSkeleton } from "@/components/RecipeCard/RecipeCardSkeleton";

export type RecipeGridModuleType = Modul_Rezept_Grid;

interface RecipesGridProps {
  data: RecipeGridModuleType;
  initialData: RecipeGridResponseType | null;
  filterOptions: SortedCategoriesType;
  fallback_image: ImageType | false;
}

const RecipeGridMoudle = ({
  data,
  // initialData,
  filterOptions,
  fallback_image,
}: RecipesGridProps) => {
  const {
    id,
    background_color_class,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
  } = data.layout;
  const { headline, headline_html_tag, posts_per_page, type } = data.grid;

  const router = useRouter();
  const { getLabel } = useGlobalContext();

  const renderContent = (
    loading: boolean,
    items: RecipeElementType[],
    sortType: TipTopfSortType,
  ) => {
    if (loading && items.length === 0) {
      const skeletons = Array.from({ length: posts_per_page }).map((_, idx) => (
        <RecipeCardSkeleton key={`initial-${idx}`} />
      ));
      return <>{skeletons}</>;
    }

    return (
      <>
        {items.map((recipe) => (
          <GridItem key={`Rezept-${recipe.recipeId}-${sortType}`}>
            <RecipeCard
              recipe={recipe}
              htmlTag={headline_html_tag === "h3" ? 4 : 3}
              needsTotalLikeCount={false}
            />
          </GridItem>
        ))}

        {loading && (
          <>
            {Array.from({ length: posts_per_page }).map((_, idx) => (
              <RecipeCardSkeleton key={`load-more-${idx}`} />
            ))}
          </>
        )}
      </>
    );
  };

  return (
    <Section
      id={id}
      disableColumns
      className={`${background_color_class} ${section_padding_class} ${section_margin_class}`}
    >
      {headline && (
        <Headline
          headline={headline}
          headline_html_tag={headline_html_tag}
          extraClass={headline_text_align_class}
        />
      )}

      <Grid
        postsPerPage={posts_per_page}
        initialFilters={getFiltersFromBrowser(router.query)}
        initialData={{ posts: [], totalPosts: 0 }}
        fetchItems={({ page, sortType, filters, produkttyp, postsPerPage }) =>
          fetchRecipes(
            getLabel,
            data.grid,
            postsPerPage ? postsPerPage : posts_per_page,
            page,
            sortType,
            filters,
            fallback_image,
            produkttyp,
          )
        }
      >
        {(gridElements, sortType, loading) => (
          <>
            <GridFilters
              showFilterButton={type === "show-all"}
              filterOptions={filterOptions.print_categories.map((cat) => {
                return {
                  kategorie_id: cat.rezept_print_kategorie_id,
                  kategorie_titel: cat.print_kategorie,
                };
              })}
              sortOptions={[
                // todo: this should come from backend later
                {
                  value: "sort_aktuell",
                  label: "Neueste",
                },
                {
                  value: "sort_bewertung",
                  label: "Bewertung",
                },
                {
                  value: "sort_az",
                  label: "A bis Z",
                },
                {
                  value: "sort_za",
                  label: "Z bis A",
                },
              ]}
            />

            <GridContent>
              {renderContent(
                loading,
                gridElements.filter((x) => !x.isClickOut),
                sortType,
              )}
            </GridContent>

            {gridElements.filter((x) => x.isClickOut).length ? (
              <>
                <h2 className="has-text-centered mt-6">
                  {getLabel("tiptopf_result_title")}
                </h2>

                <GridContent>
                  {renderContent(
                    loading,
                    gridElements.filter((x) => x.isClickOut),
                    sortType,
                  )}
                </GridContent>
              </>
            ) : (
              <></>
            )}

            <GridLoadMoreButton
              hoverStyle={
                background_color_class === "has-weiss-bg"
                  ? "pistachio"
                  : "white"
              }
            />
          </>
        )}
      </Grid>
    </Section>
  );
};

export default RecipeGridMoudle;
